import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";

export const useDeleteSeason = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const request = useApi();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  return useMutation<DefaultOperationSuccessResponse, Error, string>({
    mutationFn: async (seasonId: string) => {
      const url = `${sport}/o/${organizationId}/seasons/${seasonId}`;
      return await request(url, {
        method: "DELETE",
      });
    },
    onSuccess: async (_, seasonId) => {
      queryClient.setQueryData(["permission-scope-current-user", sport, organizationId], (oldScope) => {
        if (!oldScope) {
          return;
        }

        const scopeToUpdate = JSON.parse(JSON.stringify(oldScope));
        const seasonToDelete = scopeToUpdate.resources?.seasons?.[seasonId];

        if (seasonToDelete) {
          return scopeToUpdate.scope?.leagueTree?.map((league) => {
            return {
              ...league,
              competitions: league.competitions.map((competition) => {
                return { ...competition, seasonIds: competition.seasonIds.filter((id) => id !== seasonId) };
              }),
            };
          });
        }

        return oldScope;
      });

      notification.success({
        message: formatMessage({ id: "season.delete.success" }),
        placement: "bottomRight",
      });

      await invalidateCache();
    },
  });
};
